import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { Search } from "@/components/search/Search.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { RecognitionGiftSentModal } from "@/pages/settings/recognitions/widgets/RecognitionGiftSentModal.tsx";
import { useEmployeeRecognitionGift } from "@/queries/employees/use-employee-recognitions.query.ts";
import { useRecognitionCategories, useRecognitions } from "@/queries/use-recognitions.query.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import { ApiRecognition } from "@/types/recognitions/recognition.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Box,
  Button,
  ButtonIcon,
  EmptyStateBase,
  FontAwesomeIcon,
  Input,
  ListItem,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Text,
  TextTruncated,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

type GiftStep = "categories" | "recognitions" | "form";

const PREVIEW_SIZE = 3;

const giftSchema = z.object({
  category_id: z.number().nullable(),
  recognition_id: z.number().nullable(),
  comment: z.string(),
});
type GiftSchema = z.infer<typeof giftSchema>;

export interface EmployeeRecognitionGiftModalProps extends ModalProps {
  employee: ApiEmployee;
}

export function EmployeeRecognitionGiftModal({
  employee,
  ...props
}: EmployeeRecognitionGiftModalProps) {
  const { t } = useTranslation();
  const { openModal, close } = useModals();
  const { renderEmployeeName } = useFormatter();
  const { id } = props;

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<GiftSchema>({
    resolver: zodResolver(giftSchema),
    defaultValues: { category_id: null, recognition_id: null, comment: "" },
  });

  const [search, setSearch] = useState("");
  const categoryId = useWatch({ control, name: "category_id" });
  const recognitionId = useWatch({ control, name: "recognition_id" });

  const { data: categories, isLoading: categoriesLoading } = useRecognitionCategories();
  const { data: recognitions, isLoading: recognitionsLoading } = useRecognitions();

  let step: GiftStep = "categories";
  if (recognitionId !== null) step = "form";
  else if (categoryId !== null) step = "recognitions";

  const selected = useMemo(
    () =>
      recognitionId !== null
        ? ((recognitions ?? []).find((r) => r.id === recognitionId) ?? null)
        : null,
    [recognitions, recognitionId]
  );

  const query = search.trim().toLowerCase();

  const giftable = useMemo(
    () =>
      (recognitions ?? [])
        .filter(
          (recognition) =>
            recognition.can_gift &&
            !recognition.is_archived &&
            recognition.title.toLowerCase().includes(query)
        )
        .sort((a, b) => a.sort - b.sort),
    [recognitions, query]
  );

  const groups = useMemo(
    () =>
      (categories ?? [])
        .filter((category) => !category.is_archived)
        .map((category) => ({
          category,
          items: giftable.filter((recognition) => recognition.category_id === category.id),
        }))
        .filter((group) => group.items.length > 0),
    [categories, giftable]
  );

  const items = useMemo(
    () => giftable.filter((recognition) => recognition.category_id === categoryId),
    [giftable, categoryId]
  );

  const openCategory = (id: number) => setValue("category_id", id);

  const openRecognition = (id: number) => {
    setValue("recognition_id", id);
    setValue("comment", "");
  };

  const goBack = () => {
    if (recognitionId !== null) {
      setValue("recognition_id", null);
      return;
    }
    setValue("category_id", null);
    setSearch("");
  };

  const gift = useEmployeeRecognitionGift(control);

  const onSubmit = handleSubmit(async (data) => {
    if (data.recognition_id === null || !selected) return;
    try {
      await gift.mutateAsync({
        employeeId: employee.id,
        payload: { recognition_id: data.recognition_id, comment: data.comment.trim() || null },
      });
      close(id);
      openModal(RecognitionGiftSentModal, {
        recognition: selected,
        recipientName: renderEmployeeName(employee),
      });
    } catch {}
  });

  const renderEmpty = () => (
    <EmptyStateBase
      title={query ? t("common:nothing_found") : t("recognition:gift_no_available_title")}
    />
  );

  const renderGrid = (list: ApiRecognition[]) => (
    <Box className={"flex flex-wrap gap-x-4 gap-y-6"}>
      {list.map((recognition) => (
        <Stack
          key={recognition.id}
          gap={"smd"}
          className={"w-41 cursor-pointer"}
          onClick={() => openRecognition(recognition.id)}
        >
          <RecognitionBadge template={recognition} size={"md"} />
          <TextTruncated title={recognition.title} className={"text-title"} />
        </Stack>
      ))}
    </Box>
  );

  const renderCategories = () => {
    if (categoriesLoading || recognitionsLoading) return <Skeleton className={"h-40 w-full"} />;
    if (groups.length === 0) return renderEmpty();
    return (
      <Stack gap={"2xl"}>
        {groups.map((group) => (
          <Stack key={group.category.id} gap={"md"}>
            <ListItem
              behavior={"card"}
              title={group.category.title}
              caption={group.category.description}
              valueSlot={
                <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                  <Text className={"text-default whitespace-nowrap"}>
                    {t("recognition:recognitions_count", { count: group.items.length })}
                  </Text>
                  <ButtonIcon
                    variant={"plain"}
                    icon={<FontAwesomeIcon icon={"fa-light fa-chevron-right"} />}
                  />
                </Stack>
              }
              onClick={() => openCategory(group.category.id)}
            />
            {renderGrid(group.items.slice(0, PREVIEW_SIZE))}
          </Stack>
        ))}
      </Stack>
    );
  };

  const renderRecognitions = () => {
    if (items.length === 0) return renderEmpty();
    return renderGrid(items);
  };

  const renderForm = () => {
    if (!selected) return null;
    return (
      <Stack>
        <RecognitionBadge template={selected} size={"lg"} />
        <Title
          size={"lg"}
          paddingTop={true}
          paddingBottom={true}
          header={selected.title}
          caption={selected.description}
        />
        <Stack gap={"xs"}>
          <Title size={"xs"} header={t("recognition:comment")} paddingTop paddingBottom />
          <Input
            type={"text"}
            label={t("recognition:gift_comment_placeholder")}
            {...register("comment")}
            error={errors.comment?.message}
          />
        </Stack>
      </Stack>
    );
  };

  const renderContent = () => {
    if (step === "form") return renderForm();
    return (
      <Stack gap={"md"}>
        <Search
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          type={"text"}
          placeholder={t("recognition:gift_search_placeholder")}
          clearable={true}
          onClear={() => setSearch("")}
        />
        {step === "categories" ? renderCategories() : renderRecognitions()}
      </Stack>
    );
  };

  return (
    <Modal
      {...props}
      title={t("recognition:gift_modal_title")}
      withCloser
      size={"lg"}
      layout={"base"}
      onBack={step !== "categories" ? goBack : undefined}
      actions={
        step === "form" ? (
          <Stack gap={"sm"}>
            <ServerErrorField errors={errors} />
            <Button
              type={"submit"}
              size={"lg"}
              className={"w-full"}
              isLoading={gift.isPending}
              onClick={onSubmit}
            >
              {t("recognition:gift_send")}
            </Button>
          </Stack>
        ) : undefined
      }
    >
      <>{renderContent()}</>
    </Modal>
  );
}
